import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ItemDetails } from 'components/ItemDetails';
import { Screens } from 'components/Navigation/types';
import { LocalThumbnail } from 'components/Thumbnail/LocalThumbnail';
import { useSelector } from 'hooks/useSelector';
import { getOptionFieldResultDisplayValues, hasValue } from 'lib/fields';
import { pluralise } from 'lib/pluralise';
import {
  filterArrayEmpty,
  formatDate,
  rbAddReferencePrefix,
} from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { selectAssetFieldRepeatableInstance } from 'store/assets/selectors';
import { FieldGroupWithCalculatedInfo } from 'store/organisations/types';
import equal from 'fast-deep-equal';
import {
  doAssetDeleteFieldInstance,
  doAssetOpenFieldInstance,
} from 'store/actions';
import { useDispatch } from 'react-redux';
import { FieldTypes } from 'store/assets/types';
import { UI_DATE_FORMAT_SHORT } from 'lib/constants';

type Props = {
  id: string;
  assetId: string;
  group: FieldGroupWithCalculatedInfo;
  isEditable?: boolean;
};

export const FieldRepeatableListItem: React.FC<Props> = React.memo(
  ({ id, assetId, group, isEditable }) => {
    const dispatch = useDispatch();
    const instance = useSelector(
      (state) => selectAssetFieldRepeatableInstance(state, assetId, id),
      equal,
    );

    if (!instance) {
      return null;
    }

    const { imageFieldId } = group.calculatedInfo;

    const imageId: string | undefined = imageFieldId
      ? instance.results[imageFieldId]?.value?.[0]
      : undefined;

    const resultsCount = Object.values(instance.results).filter(
      hasValue,
    ).length;

    const additionalTitles = group.fieldIds.reduce<string[]>(
      (acc, fieldId) => {
        const field = group.fields[fieldId]!;
        const result = instance.results[fieldId];
        if (result) {
          if (field.type === FieldTypes.OPTION) {
            const optionValues = getOptionFieldResultDisplayValues(
              field,
              result,
              true,
            ).join(', ');
            if (
              optionValues &&
              optionValues !== 'Yes' &&
              optionValues !== 'No'
            ) {
              acc.push(optionValues);
            }
          } else if (
            field.type === FieldTypes.TEXT ||
            field.type === FieldTypes.NOTE
          ) {
            if (result.value?.length) {
              acc.push(result.value as string);
            }
          }
        }
        return acc;
      },
      group.disableAutoId ? [] : [instance.formattedAutoId!],
    );

    const hasQr =
      group.calculatedInfo.qrFieldId &&
      hasValue(instance.results[group.calculatedInfo.qrFieldId]);
    const hasPin =
      group.calculatedInfo.pinFieldId &&
      hasValue(instance.results[group.calculatedInfo.pinFieldId]);
    const createdFormatted = formatDate(
      instance.createdTs,
      UI_DATE_FORMAT_SHORT,
    );
    const updatedFormatted = formatDate(
      instance.updatedTs,
      UI_DATE_FORMAT_SHORT,
    );

    return (
      <Card style={{ marginBottom: theme.paddingHorizontalHalf }}>
        <CardRow
          onPress={() =>
            dispatch(
              doAssetOpenFieldInstance({
                assetId,
                groupId: group.id,
                instanceId: id,
              }),
            )
          }
          navigationIsSelected={{
            name: Screens.ASSET_FIELDREPEATABLE,
            params: {
              groupId: group.id,
              instanceId: id,
            },
          }}
          indentActions
          menuOptions={
            isEditable
              ? [
                  {
                    text: `Delete ${group.calculatedInfo.singularName}`,
                    onPress: () =>
                      dispatch(
                        doAssetDeleteFieldInstance({
                          assetId,
                          instanceId: instance.instanceId,
                        }),
                      ),
                    icon: ICONS.TrashCanLight,
                    destructive: true,
                  },
                ]
              : undefined
          }
        >
          <View
            style={{
              alignItems: 'center',
              flex: 1,
              flexDirection: 'row',
              justifyContent: 'center',
            }}
          >
            {!!imageFieldId && (
              <View style={{ marginRight: theme.paddingHorizontal, width: 52 }}>
                <LocalThumbnail
                  width={52}
                  height={52}
                  rounded
                  thumbnailId={imageId}
                />
              </View>
            )}
            <ItemDetails
              title={additionalTitles.join(' • ') || 'No Value'}
              subtitle={[
                instance.reference
                  ? rbAddReferencePrefix(instance.reference)
                  : null,
                instance.draft !== undefined
                  ? instance.draft
                    ? 'New'
                    : ''
                  : !instance.autoId
                    ? 'New'
                    : '',
                pluralise(
                  resultsCount,
                  '1 Result',
                  '{x} Results',
                  'No Results',
                ),
              ]
                .filter(filterArrayEmpty)
                .join(' • ')}
              subsubtitle={`${createdFormatted}${
                updatedFormatted !== createdFormatted
                  ? `, Updated ${updatedFormatted}`
                  : ''
              }`}
              subtitleIcons={[
                hasQr ? ICONS.QRCodeLight : null,
                hasPin ? ICONS.LocationPinLight : null,
              ].filter(filterArrayEmpty)}
            />
          </View>
        </CardRow>
      </Card>
    );
  },
);
